import { IconName } from "@subframe/core";
import React, { createContext, useContext, useState, useEffect } from "react";
import { Alert, IconButton } from "subframe";
import { AlertVariant } from "../types";

interface NotificationParams {
  title: string;
  description?: string;
  variant?: AlertVariant;
  icon?: IconName
}

interface NotificationContextType {
  showNotification: (params: NotificationParams) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

const getDefaultIcon = (variant?: AlertVariant): IconName => {
  switch (variant) {
    case AlertVariant.SUCCESS:
      return "FeatherCheckCircle";
    case AlertVariant.ERROR:
      return "FeatherAlertCircle";
    case AlertVariant.WARNING:
      return "FeatherAlertTriangle";
    case AlertVariant.BRAND:
      return "FeatherStar";
    case AlertVariant.NEUTRAL:
    default:
      return "FeatherInfo";
  }
};

// Global notification function
let globalShowNotification: ((params: NotificationParams) => void) | null = null;

export const notify = (params: NotificationParams) => {
  if (globalShowNotification) {
    globalShowNotification(params);
  } else {
    console.warn('NotificationProvider not initialized yet');
  }
};

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [visible, setVisible] = useState(false);
  const [alertProps, setAlertProps] = useState<NotificationParams>({
    title: "",
    description: "",
    variant: AlertVariant.NEUTRAL,
  });

  const showNotification = (params: NotificationParams) => {
    setAlertProps(params);
    setVisible(true);

    setTimeout(() => {
      setVisible(false);
    }, 4000);
  };

  useEffect(() => {
    globalShowNotification = showNotification;

    return () => {
      globalShowNotification = null;
    };
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {visible && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md w-full">
          <Alert
            variant={alertProps.variant || AlertVariant.NEUTRAL}
            icon={alertProps.icon || getDefaultIcon(alertProps.variant)}
            title={alertProps.title}
            description={alertProps.description}
            actions={
              <IconButton
                size="medium"
                icon="FeatherX"
                onClick={() => setVisible(false)}
              />
            }
          />
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotification must be used within NotificationProvider");
  return context;
};
